/**
 * Уровни детализации чертежа при отдалении — как на картах.
 *
 * Подписи, значки розеток и узлы стен держат один экранный размер (`n * mmPerPx`), а комнаты
 * при отдалении сжимаются, и всё налезает друг на друга. Поэтому мелочь уходит постепенно:
 * — подпись комнаты: влезает «площадь и название» — пишем всё, влезает одно название —
 *   только его, не влезает ничего — прячем;
 * — размер стороны прячется, когда его подпись не помещается на своём отрезке;
 * — значки на стенах и потолке, кабели, трубы и узлы уходят на обзорном масштабе,
 *   когда метр на экране меньше `OVERVIEW_PX_PER_M` пикселей.
 *
 * Прячем прозрачностью с меткой `data-lod-hidden`, а не убираем из разметки: экспорт
 * в PDF клонирует живой холст и метку снимает — на листе чертёж полный при любом зуме.
 */
import type { CSSProperties } from "react";
import { formatLength } from "@/shared/lib/geometry";
import { roomLabelSize } from "./roomLabelPlacement";
import type { Room } from "./rooms";

/** Меньше стольких пикселей на метр — обзорный масштаб: мелочь на стенах и потолке прячем. */
const OVERVIEW_PX_PER_M = 40;
/** Кегль размерной подписи, пикселей — как у `DimensionLine`. */
const DIMENSION_FONT_PX = 11;
/** Запас вокруг подписи, чтобы она не касалась засечек и стен, пикселей. */
const LABEL_MARGIN_PX = 14;

/** Обзорный масштаб: план целиком мелкий, детали только мешают. */
export const isOverviewScale = (mmPerPx: number) => 1000 / mmPerPx < OVERVIEW_PX_PER_M;

/** Что писать в комнате при этом масштабе. */
export type RoomLabelDetail = "full" | "name" | "none";

export const roomLabelDetail = (
	room: Room,
	lines: { area: string; name: string; ceiling?: string },
	mmPerPx: number,
): RoomLabelDetail => {
	const xs = room.inner.map((point) => point.x);
	const ys = room.inner.map((point) => point.y);
	const width = Math.max(...xs) - Math.min(...xs);
	const height = Math.max(...ys) - Math.min(...ys);
	const margin = LABEL_MARGIN_PX * mmPerPx;
	const fits = (text: string, count: number) => {
		const size = roomLabelSize(text, mmPerPx, count);
		return size.width + margin <= width && size.height + margin <= height;
	};
	const longest = [lines.area, lines.name, lines.ceiling ?? ""].reduce((a, b) =>
		a.length >= b.length ? a : b,
	);
	if (fits(longest, lines.ceiling ? 3 : 2)) return "full";
	if (fits(lines.name, 1)) return "name";
	return "none";
};

/** Помещается ли подпись размера на своём отрезке длиной `length` мм. */
export const dimensionLabelFits = (length: number, mmPerPx: number) =>
	formatLength(length).length * DIMENSION_FONT_PX * 0.6 * mmPerPx + LABEL_MARGIN_PX * mmPerPx <=
	length;

/** Свойства группы, которую масштаб прячет: прозрачность с плавным переходом и метка для экспорта. */
export const lodProps = (hidden: boolean) => ({
	"data-lod-hidden": hidden ? "" : undefined,
	style: {
		opacity: hidden ? 0 : 1,
		transition: "opacity 160ms ease-out",
		pointerEvents: hidden ? "none" : undefined,
	} satisfies CSSProperties,
});
